import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

export const fetchWaiterRequests = createAsyncThunk(
  'waiterRequests/fetchWaiterRequests',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.waiterRequests.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 2 * 60 * 1000) { // 2 minutes cache for real-time updates
        return state.waiterRequests.items;
      }
      
      const response = await axios.get(API_ENDPOINTS.WAITER_REQUESTS_BY_RESTAURANT(restaurantId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch waiter requests');
    }
  }
);

export const createWaiterRequest = createAsyncThunk(
  'waiterRequests/createWaiterRequest',
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.WAITER_REQUESTS, requestData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create waiter request');
    }
  }
);

export const updateWaiterRequest = createAsyncThunk(
  'waiterRequests/updateWaiterRequest',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.WAITER_REQUEST_UPDATE(id), updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update waiter request');
    }
  }
);

export const fetchPendingRequestsCount = createAsyncThunk(
  'waiterRequests/fetchPendingRequestsCount',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.waiterRequests.pendingCountLastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 1 * 60 * 1000) { // 1 minute cache for count
        return state.waiterRequests.pendingCount;
      }
      
      const response = await axios.get(API_ENDPOINTS.WAITER_REQUESTS_PENDING_COUNT(restaurantId));
      return response.data.count;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch pending requests count');
    }
  }
);

export const fetchCustomerWaiterRequests = createAsyncThunk(
  'waiterRequests/fetchCustomerWaiterRequests',
  async ({ customerMobile, restaurantId }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const existingRequests = state.waiterRequests.customerRequests[`${customerMobile}-${restaurantId}`];
      const lastFetched = existingRequests?.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 5 * 60 * 1000) {
        return existingRequests;
      }
      
      const response = await axios.get(API_ENDPOINTS.CUSTOMER_WAITER_REQUESTS(customerMobile, restaurantId));
      return { key: `${customerMobile}-${restaurantId}`, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer waiter requests');
    }
  }
);

const initialState = {
  items: [],
  customerRequests: {},
  pendingCount: 0,
  loading: false,
  error: null,
  lastFetched: null,
  pendingCountLastFetched: null,
};

const waiterRequestSlice = createSlice({
  name: 'waiterRequests',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
      state.pendingCountLastFetched = null;
    },
    addWaiterRequest: (state, action) => {
      state.items.unshift(action.payload);
      if (action.payload.status === 'Pending') {
        state.pendingCount += 1;
      }
    },
    updateWaiterRequestStatus: (state, action) => {
      const { id, status } = action.payload;
      const request = state.items.find(r => r._id === id);
      if (request) {
        const wasPending = request.status === 'Pending';
        request.status = status;
        
        if (wasPending && status !== 'Pending') {
          state.pendingCount = Math.max(0, state.pendingCount - 1);
        } else if (!wasPending && status === 'Pending') {
          state.pendingCount += 1;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaiterRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaiterRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchWaiterRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createWaiterRequest.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        if (action.payload.status === 'Pending') {
          state.pendingCount += 1;
        }
      })
      .addCase(updateWaiterRequest.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          const oldStatus = state.items[index].status;
          const newStatus = action.payload.status;
          
          state.items[index] = action.payload;
          
          if (oldStatus === 'Pending' && newStatus !== 'Pending') {
            state.pendingCount = Math.max(0, state.pendingCount - 1);
          } else if (oldStatus !== 'Pending' && newStatus === 'Pending') {
            state.pendingCount += 1;
          }
        }
      })
      .addCase(fetchPendingRequestsCount.fulfilled, (state, action) => {
        state.pendingCount = action.payload;
        state.pendingCountLastFetched = Date.now();
      })
      .addCase(fetchCustomerWaiterRequests.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.customerRequests[key] = {
          ...data,
          lastFetched: Date.now()
        };
      });
  },
});

export const selectAllWaiterRequests = (state) => state.waiterRequests.items;
export const selectWaiterRequestLoading = (state) => state.waiterRequests.loading;
export const selectWaiterRequestError = (state) => state.waiterRequests.error;
export const selectPendingRequestsCount = (state) => state.waiterRequests.pendingCount;
export const selectCustomerWaiterRequests = (state, customerMobile, restaurantId) => 
  state.waiterRequests.customerRequests[`${customerMobile}-${restaurantId}`];

export const selectPendingWaiterRequests = (state) => 
  state.waiterRequests.items.filter(request => request.status === 'Pending');

export const selectCompletedWaiterRequests = (state) => 
  state.waiterRequests.items.filter(request => request.status === 'Completed');

export const { 
  clearError, 
  invalidateCache, 
  addWaiterRequest, 
  updateWaiterRequestStatus 
} = waiterRequestSlice.actions;

export default waiterRequestSlice.reducer;

